'use client';

import Image from 'next/image';
import Link from 'next/link';
import { getSocialLinks } from '@/lib/socialLinks';
import { useAuth } from '@/contexts/AuthContext';
import { isServicesPublic } from '@/lib/featureFlags';
import { MEMBER_GUIDE_LABEL, MEMBER_GUIDE_PDF_PATH } from '@/lib/memberGuide';

const nav = [
  { href: '/', label: 'ホーム' },
  { href: '/about', label: '結会とは' },
  { href: '/events', label: '各種イベント' },
  ...(isServicesPublic() ? [{ href: '/services', label: 'サービス' }] : []),
  { href: '/members', label: '会員紹介' },
  { href: '/clubs', label: '部活' },
  { href: '/recruitment', label: '求人サポート' },
  { href: '/contact', label: 'お問い合わせ' },
  { href: '/register', label: '会員申込' },
  { href: '/blog', label: 'ブログ' },
];

export default function Footer() {
  const socials = getSocialLinks();
  const { user, loading } = useAuth();
  // 目的: ログイン中は「会員申込」などのCTA導線を非表示にする
  const navItems =
    !loading && user ? nav.filter((n) => n.href !== '/register') : nav;
  return (
    <footer className='relative w-full bg-yui-gradient pt-8'>
      <div className='mx-auto max-w-7xl px-6'>
        <div className='grid gap-8 md:grid-cols-12 md:items-start md:gap-10'>
          <div className='w-full md:col-span-4'>
            <h3 className='font-serif text-2xl text-gray-800'>
              結会 / Yui Kai
            </h3>
            <p className='mt-2 text-[16px] text-gray-700/80'>
              女性のための人結びコミュニティ
            </p>
          </div>

          <nav className='w-full md:col-span-6'>
            <ul className='grid grid-cols-2 gap-x-8 gap-y-3 sm:grid-cols-3 md:grid-cols-3 md:gap-x-10 md:gap-y-4'>
              {navItems.map((n) => (
                <li key={n.href}>
                  <Link
                    href={n.href}
                    className='group relative inline-block text-sm tracking-tight text-gray-800 sm:text-[16px]'
                  >
                    <span className='font-serif'>{n.label}</span>
                    <span className='absolute -bottom-1 left-0 h-0.5 w-0 bg-secondary transition-all duration-200 group-hover:w-full' />
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className='flex w-full items-center justify-center gap-4 md:col-span-2 md:justify-end'>
            {socials.map((s) => (
              <a
                key={s.key}
                aria-label={s.label}
                href={s.href}
                target={s.href === '#' ? undefined : '_blank'}
                rel={s.href === '#' ? undefined : 'noopener noreferrer'}
                className='inline-flex size-10 items-center justify-center rounded-sm bg-gray-200/40 transition hover:bg-gray-200/60'
              >
                <Image src={s.iconSrc} alt='' width={20} height={20} />
              </a>
            ))}
          </div>
        </div>

        <div className='mt-8 border-t border-gray-200/70 py-6 text-center'>
          <div className='mb-4 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-6'>
            <Link
              href='/legal'
              className='group relative text-sm tracking-tight text-gray-800 sm:text-[16px]'
            >
              <span className='font-serif'>特定商取引法に基づく表記</span>
              <span className='absolute -bottom-1 left-0 h-0.5 w-0 bg-secondary transition-all duration-200 group-hover:w-full' />
            </Link>
            <Link
              href='/terms'
              className='group relative text-sm tracking-tight text-gray-800 sm:text-[16px]'
            >
              <span className='font-serif'>利用規約</span>
              <span className='absolute -bottom-1 left-0 h-0.5 w-0 bg-secondary transition-all duration-200 group-hover:w-full' />
            </Link>
            <Link
              href='/privacy'
              className='group relative text-sm tracking-tight text-gray-800 sm:text-[16px]'
            >
              <span className='font-serif'>プライバシーポリシー</span>
              <span className='absolute -bottom-1 left-0 h-0.5 w-0 bg-secondary transition-all duration-200 group-hover:w-full' />
            </Link>
            {/*
              PDFは Next のルートではなく public/ の静的ファイルなので、next/link ではなく <a> で開く。
              next/link はクライアント遷移を試みてから落ちるため、体感が一拍遅れる
            */}
            <a
              href={MEMBER_GUIDE_PDF_PATH}
              target='_blank'
              rel='noopener noreferrer'
              className='group relative text-sm tracking-tight text-gray-800 sm:text-[16px]'
            >
              <span className='font-serif'>{MEMBER_GUIDE_LABEL}（PDF）</span>
              <span className='absolute -bottom-1 left-0 h-0.5 w-0 bg-secondary transition-all duration-200 group-hover:w-full' />
            </a>
          </div>
          <p className='text-[14px] text-gray-800'>
            © 2024 結会 (Yui Kai). All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
